import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react'
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
// utils and helpers
import { openSnackbar } from "../../redux/slices/common";
import { openCustomModalDialog } from "../../redux/slices/modalSlice";
// custom components
import NoDataFound from "common/component/NoDataFound";
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";

const defaultPage = {
  page: 1,
  limit: 20
}

const InfiniteScrollTable = ({
  id = "",
  columnDefs,
  refreshTable,
  getDataMethod = () => { },
  customFilters = {},
  onSelectionChange = () => { },
  showDeleteBoxButton = false,
  handleDeleteBox = () => { },
  tableHeight = 450,
  setSelectedBoxes = () => { },
}) => {
  const dispatch = useDispatch()

  const [paginationDetails, setPaginationDetails] = useState({ ...defaultPage });
  const [hasMore, setHasMore] = useState(true);
  const [childrenList, setChildrenList] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ sort: "", order: "" });

  const handleGetData = useCallback(async (filters = {}) => {
    setIsLoading(true)
    try {
      if (!getDataMethod) return;

      const request = { ...customFilters, ...paginationDetails, ...sortConfig, ...filters };

      const response = await dispatch(getDataMethod(request)).unwrap();
      const data = response?.data || response?.products

      if (data) {
        let myData = {};
        if (request.page === 1) {
          myData = { ...response, data }
        } else {
          myData = { ...response, data: [...(childrenList?.data || []), ...data] }
        }

        setChildrenList(myData);

        const hasMoreData = response?.total > (myData?.data?.length || 0);
        if (hasMoreData) {
          setPaginationDetails(prev => ({ ...prev, page: prev.page + 1 }));
          setHasMore(true);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsLoading(false);
    }
  }, [customFilters, dispatch, getDataMethod, paginationDetails, sortConfig]);

  const handleSort = (field) => {
    let newDir = "asc";
    if (sortConfig.sort === field) {
      if (sortConfig.order === "asc") newDir = "desc";
      else if (sortConfig.order === "desc") newDir = "";
    }

    const newConfig = { sort: newDir ? field : "", order: newDir };
    setSortConfig(newConfig);
    setChildrenList({});
    setPaginationDetails({ ...defaultPage });
    handleGetData({ ...defaultPage, ...newConfig });
  };

  const handleSelectAll = (e, boxId) => {
    const { checked } = e.target;

    setSelectedRows(prev => {
      const updated = [...prev];
      const index = updated?.findIndex((item) => item.boxId === boxId);

      if (index >= 0) {
        updated[index] = { ...updated[index], boxId, isSelectedAll: checked, products: [] }
      } else {
        updated.push({ boxId, isSelectedAll: checked, products: [] });
      }

      onSelectionChange(updated);
      return updated;
    });
  };

  const handleRowSelection = (e, rowId, boxId) => {
    const { checked } = e.target;

    setSelectedRows(prev => {
      let updatedRows = [...prev];

      const index = updatedRows?.findIndex((item) => item.boxId === boxId);
      if (index >= 0) {
        let products = [];

        if (updatedRows[index]?.isSelectedAll) {
          const checkboxColumn = memoizedColumnDefs?.find(col => col.checkboxSelection);
          if (checkboxColumn && childrenList?.data) {
            products = childrenList.data
              .map(row => (checkboxColumn.setRowId({ data: row })))
              .filter(id => id !== rowId);
          }
        } else if (checked) {
          products = [...(updatedRows[index].products || []), rowId];
        } else {
          products = updatedRows[index]?.products?.filter(item => item !== rowId)
        }

        updatedRows[index] = {
          ...updatedRows[index],
          boxId,
          isSelectedAll: false,
          products: products
        }
      } else {
        updatedRows.push({ boxId, isSelectedAll: false, products: [rowId] });
      }

      // Notify parent of selection change
      onSelectionChange(updatedRows);
      return updatedRows;
    });
  };

  useEffect(() => {
    setChildrenList({});
    setPaginationDetails({ ...defaultPage });
    handleGetData({ ...defaultPage })
  }, [refreshTable]);

  const memoizedColumnDefs = useMemo(() => columnDefs, [columnDefs]);

  return (
    <>
      <Grid container alignItems='center' className="table-with-accordion">
        <div
          className="cus-ag-grid"
          style={{ height: tableHeight, width: "100%", overflowY: "auto" }}
          id={id || "infiniteScrollTable"}
        >
          {
            isLoading && paginationDetails?.page === 1
              ? <TableLoader />
              : <InfiniteScroll
                dataLength={childrenList?.data?.length || 0}
                next={handleGetData}
                hasMore={hasMore}
                loader={
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                    <CircularProgress size={32} />
                  </div>
                }
                scrollableTarget={id || "infiniteScrollTable"}
              >
                {
                  (childrenList?.data?.length || 0) > 0
                    ? childrenList?.data?.map((data, idx, arr) => {

                      // Determine if this is the first row for a new box
                      const prevBox = idx > 0 ? arr[idx - 1]?.box?._id : null;
                      const box = data?.box?._id;
                      const isNewBox = box !== prevBox;

                      return (
                        <Fragment key={(data?._id || data?.id || idx)}>
                          {/* Insert new box */}
                          {isNewBox && (
                            <>
                              {/* Box Name */}
                              <Box className="accordion-header cus-table-row" sx={{ width: "100%", background: "#f5f5f5" }}>
                                <Typography className="cus-table-cell" style={{ fontWeight: 600, fontSize: 14, width: "100%" }}>
                                  {data?.box?.name || "-"} {data?.box?.totalProducts ? `(${data?.box?.totalProducts})` : null}
                                </Typography>
                                {
                                  showDeleteBoxButton &&
                                  <Box display="flex" flexDirection="row" gap={1} className="field-actions">
                                    <IconButton
                                      size='small'
                                      variant='primary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(openCustomModalDialog({
                                          modalData: { box: data?.box, setSelectedBoxes },
                                          modal: 'BULK_ASSIGN_QTY'
                                        }))
                                      }}
                                      style={{ fontSize: "12px", fontWeight: "600", padding: "6px 10px", boxShadow: "none", background: "#0163FF", color: "#ffffff" }}
                                    >
                                      Bulk Assign Qty
                                    </IconButton>
                                    <IconButton
                                      color='error'
                                      size='small'
                                      variant='primary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(openCustomModalDialog({
                                          modalData: { deleteHandler: () => handleDeleteBox(data) },
                                          modal: 'DELETE_MODAL'
                                        }))
                                      }}
                                      style={{ fontSize: "12px", fontWeight: "600", padding: "6px 10px", boxShadow: "none", color: "#2E2E3A" }}
                                    >
                                      {/* <DeleteIcon width='16px' height='16px' /> */}
                                      Remove Box
                                    </IconButton>
                                  </Box>
                                }
                              </Box>
                            </>
                          )}

                          <div className="table-listing">
                            {/* Table header */}
                            {isNewBox && (
                              <>
                                <Box className="cus-table-header" style={{ borderRadius: 0 }}>
                                  {/* Products header */}
                                  {
                                    memoizedColumnDefs?.map((column, idx) => {
                                      let { style = {}, minWidth, flex, headerName, field, selectedAll, sortable } = column || {};

                                      if (minWidth) {
                                        style.minWidth = minWidth;
                                      }
                                      if (flex) {
                                        style.flex = flex;
                                      }

                                      const isAllSelected = selectedRows?.find((row) => row?.boxId === data?.boxId)?.isSelectedAll;

                                      return (
                                        <>
                                          {/* Checkbox to select all */}
                                          {selectedAll && <Box className="form-check px-1 d-flex align-item-center gap-2 text-14">
                                            <input
                                              className="form-check-input cur-pointer"
                                              type="checkbox"
                                              id={`flexCheckIndeterminate`}
                                              checked={isAllSelected}
                                              onChange={e => handleSelectAll(e, data?.boxId)}
                                            />
                                          </Box>}
                                          <Typography
                                            key={headerName || field || idx}
                                            className="cus-table-cell"
                                            style={{ ...style, cursor: sortable ? "pointer" : "default", display: "flex", alignItems: "center", gap: "4px" }}
                                            onClick={() => sortable && handleSort(field)}
                                          >
                                            {headerName}
                                            {sortable && sortConfig.sort === field && (
                                              sortConfig.order === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> : <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                                            )}
                                          </Typography>
                                        </>
                                      )
                                    })
                                  }
                                </Box>
                              </>
                            )}

                            {/* Table list */}
                            <Box className="accordion-header cus-table-row" sx={{ width: "100%" }}>
                              {
                                memoizedColumnDefs?.map((column) => {
                                  let { style = {}, minWidth, flex, headerName, field, cellRenderer, valueGetter, checkboxSelection, setRowId } = column || {};
                                  const rowId = setRowId ? setRowId({ data: data }) : "";

                                  const cellValue = cellRenderer
                                    ? cellRenderer({ data: data, colDef: column })
                                    : valueGetter
                                      ? valueGetter({ data: data })
                                      : data[field];

                                  if (minWidth) {
                                    style.minWidth = minWidth;
                                  }
                                  if (flex) {
                                    style.flex = flex;
                                  }

                                  return (
                                    <>
                                      {/* Checkbox to select row */}
                                      {(() => {
                                        if (checkboxSelection && rowId) {
                                          const box = selectedRows?.find(item => item.boxId === data?.boxId) || {}
                                          const isRowSelected = box.isSelectedAll || box?.products?.some(id => id === rowId);

                                          return (<Box className="form-check px-1 d-flex align-item-center gap-2 text-14">
                                            <input
                                              className="form-check-input cur-pointer"
                                              type="checkbox"
                                              id={`flexCheckIndeterminate`}
                                              name={rowId}
                                              checked={isRowSelected}
                                              onChange={e => handleRowSelection(e, rowId, data?.boxId, data)}
                                            />
                                          </Box>)
                                        }
                                      })()}

                                      {/* Cell data */}
                                      <Typography
                                        key={`${field || headerName}-${data?._id || data?.id || idx}`}
                                        className="cus-table-cell" style={{ ...style }}
                                      >
                                        {cellValue}
                                      </Typography>
                                    </>
                                  )
                                })
                              }
                            </Box>
                          </div>
                        </Fragment>
                      )
                    })
                    : childrenList?.data?.length === 0
                      ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                        <NoDataFound />
                      </div>
                      : null
                }
              </InfiniteScroll>
          }
        </div>
      </Grid>
    </>
  )
}

export default InfiniteScrollTable